
import React from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { UserX, RefreshCw, AlertTriangle } from 'lucide-react';
import { confirm } from '@/components/ui/confirm';
import { deleteUser } from '@/services/userService';

interface DeleteUserButtonProps {
  userId: string;
  userName: string;
  isDeleting: boolean;
  onDeleteStart: (userId: string) => void;
  onDeleteComplete: (userId: string) => void;
}

export const DeleteUserButton: React.FC<DeleteUserButtonProps> = ({ 
  userId, 
  userName,
  isDeleting,
  onDeleteStart,
  onDeleteComplete
}) => {
  const { toast } = useToast();

  const handleDeleteUser = async () => {
    // Ask for confirmation
    confirm({
      title: "Delete User",
      description: (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-semibold">Warning: This action cannot be undone.</span>
          </div>
          <p>
            Are you sure you want to permanently delete {userName || "this user"}? 
            This will remove all user data and access to the system.
          </p>
        </div>
      ),
      onConfirm: async () => {
        try {
          onDeleteStart(userId);
          
          await deleteUser(userId);
          
          onDeleteComplete(userId);
          
          toast({
            title: "User deleted",
            description: "The user has been permanently deleted",
          });
        } catch (error: any) {
          toast({
            title: "Failed to delete user",
            description: error.message,
            variant: "destructive",
          });
          onDeleteStart(null); // Reset deleting state
        }
      }
    });
  };

  return (
    <Button 
      variant="ghost" 
      size="sm"
      onClick={handleDeleteUser}
      disabled={isDeleting}
      className="text-destructive hover:text-destructive hover:bg-destructive/10"
    >
      {isDeleting ? (
        <RefreshCw className="h-4 w-4 animate-spin" />
      ) : (
        <UserX className="h-4 w-4" />
      )}
    </Button>
  );
};
